import { emitAdminToast } from '../utils/adminToast';

export const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000/api');

const API_KEY = process.env.REACT_APP_API_KEY || '';
const getToken = () => localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
const setToken = (token) => {
  const storage = localStorage.getItem('admin_token') ? localStorage : sessionStorage;
  storage.setItem('admin_token', token);
};

const isFormData = (body) => typeof FormData !== 'undefined' && body instanceof FormData;

const buildHeaders = (body) => {
  const h = {};
  // Browser sets multipart boundary automatically for FormData — don't override.
  if (body && !isFormData(body)) h['Content-Type'] = 'application/json';
  if (API_KEY) h['X-API-Key'] = API_KEY;
  const token = getToken();
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

const handleRes = async (res) => {
  let data = {};
  try {
    data = await res.json();
  } catch {
    // non-JSON response (e.g. empty body)
  }
  if (!res.ok) {
    const validationMessage = Array.isArray(data.errors) ? data.errors.filter(Boolean).join(' · ') : '';
    const err = new Error(validationMessage || data.message || `Request failed with status ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

// Thin fetch wrapper.
//   request('/books')
//   request('/books/:id', { method: 'DELETE' })
//   request('/banners', { method: 'POST', body: formData })   ← FormData ok
//   request('/coupons', { method: 'POST', body: { ... } })
let refreshPromise = null;

const attemptRefresh = async () => {
  if (refreshPromise) return refreshPromise;
  refreshPromise = fetch(`${API_BASE}/auth/refresh-token`, {
    method: 'POST',
    credentials: 'include',
    headers: API_KEY ? { 'X-API-Key': API_KEY } : {},
  })
    .then(async (res) => {
      if (!res.ok) throw new Error('Refresh failed');
      const data = await res.json();
      setToken(data.token);
      return data.token;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
};

export const request = async (path, { method = 'GET', body } = {}) => {
  const options = {
    method,
    headers: buildHeaders(body),
    credentials: 'include',
    body: body == null ? undefined : isFormData(body) ? body : JSON.stringify(body),
  };
  let res = await fetch(`${API_BASE}${path}`, options);

  if (res.status === 401 && getToken()) {
    try {
      await attemptRefresh();
      res = await fetch(`${API_BASE}${path}`, { ...options, headers: buildHeaders(body) });
    } catch {
      localStorage.removeItem('admin_token');
      sessionStorage.removeItem('admin_token');
    }
  }

  try {
    const data = await handleRes(res);
    if (method !== 'GET' && data?.message) {
      emitAdminToast(data.message, data.rejected > 0 ? 'info' : 'success');
    }
    return data;
  } catch (error) {
    emitAdminToast(error.message, 'error');
    throw error;
  }
};

// Stream a file download from the API (used for XLSX export).
export const downloadFile = async (path, filename) => {
  let url;
  let anchor;
  try {
    const fetchFile = () => fetch(`${API_BASE}${path}`, {
      headers: buildHeaders(),
      credentials: 'include',
    });
    let res = await fetchFile();
    if (res.status === 401 && getToken()) {
      let refreshed = false;
      try {
        await attemptRefresh();
        refreshed = true;
      } catch {
        localStorage.removeItem('admin_token');
        sessionStorage.removeItem('admin_token');
      }
      if (refreshed) res = await fetchFile();
    }
    if (!res.ok) await handleRes(res);
    const blob = await res.blob();
    url = URL.createObjectURL(blob);
    anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
  } catch (error) {
    emitAdminToast(error.message, 'error');
    throw error;
  } finally {
    anchor?.remove();
    if (url) window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};
